import { useCallback, useRef } from "react";
// https://www.npmjs.com/package/use-subscription

import { useSubscription } from "use-subscription";

import { useAppContext } from "./useAppContext";

export function referenceComparer<T>(prevState: T, newState: T): boolean {
	return prevState === newState; // Object.is() could be used for NaN, but used '===' because it threats -0 and +0 as the same
}

/**
 * A simple way to compare 2 objects by comparing them stringified.
 * This means that the order of properties is important, and it doesn't work with circular references
 */
export function jsonComparer<T>(prevState: T, newState: T): boolean {
	if (
		typeof prevState === "object" &&
		prevState !== null &&
		typeof newState === "object" &&
		newState !== null
	) {
		try {
			return JSON.stringify(prevState) === JSON.stringify(newState);
		} catch {
			return false;
		}
	}

	return false;
}

/**
 * A simple way to compare 2 arrays by their content
 * It compares the length and checks if the same references are on the same index, it doesn't do a deep comparison of the content
 */
export function arrayComparer<T>(prevState: T, newState: T): boolean {
	// compare content
	if (Array.isArray(prevState) && Array.isArray(newState)) {
		if (prevState.length !== newState.length) return false;
		if (prevState.some((prevItem, index) => !referenceComparer(prevItem, newState[index])))
			return false; // didn't choose to do a recursive deep comparision (compareByArrayContent)
		return true;
	}

	return false;
}

/** Simple function to show first differences in data by value */
export function diffMessage(a: unknown, b: unknown, path = ""): string {
	const prefix = path ? `${path}: ` : "";
	if (typeof a !== typeof b)
		return `${prefix}different types detected: '${typeof a}' vs '${typeof b}': ${eXpress.utils.toJsonString(
			a,
		)} vs ${eXpress.utils.toJsonString(b)}`;
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length)
			return `${prefix}array has ${a.length} vs ${
				b.length
			} elements: ${eXpress.utils.toJsonString(a)} vs ${eXpress.utils.toJsonString(b)}`;
		if (a.length === 0) return "";
		for (let i = 0; i < a.length; i += 1) {
			const message = diffMessage(a[i], b[i], `${path}[${i}]`);
			if (message) return message;
		}
	}
	if (typeof a === "object" && a !== null && typeof b === "object" && b !== null) {
		const keysA = Object.keys(a);
		const keysB = Object.keys(b);
		const onlyInA = keysA.filter((keyA) => !keysB.includes(keyA)).map((k) => `'${k}'`);
		if (onlyInA.length)
			return `${prefix}object 1 has key(s) ${onlyInA.join(", ")} that are not in object 2`;

		const onlyInB = keysB.filter((keyB) => !keysA.includes(keyB)).map((k) => `'${k}'`);
		if (onlyInB.length)
			return `${prefix}object 2 has key(s) ${onlyInB.join(", ")} that are not in object 1`;

		if (keysA.length === 0) return "";
		for (let i = 0; i < keysA.length; i += 1) {
			const key = keysA[i];

			const message = diffMessage((a as any)[key], (b as any)[key], `${path}["${key}"]`);
			if (message) return message;
		}
	}
	if (a !== b) {
		return `${prefix}${eXpress.utils.toJsonString(
			a,
		)} is different from ${eXpress.utils.toJsonString(b)}`;
	}
	return "";
}

/**
 * Subscribe for messages and unsubscribe on unmount.
 */
export function useMessageToState<T>(options: {
	/** The name of the hook it is used in, for debugging messages */
	name: string;
	/**
	 * For performance reasons, only check state for these selected messages
	 * Don't depend on state in this callback, because it is execute synchronous (before React state is updated)
	 */
	filterMessage?(message: eXpress.core.IMessage): boolean;

	/**
	 * A function to get the state. \
	 * Remark: \
	 * - Make this function stable
	 * - Make the result stable or use a comparer function to prevent render loop
	 * - Should we pass the previous value so the caller return the same reference if state is not changed (example an equal array)
	 */
	getState(editor: eXpress.core.IEditor): T;

	/**
	 * Comparer to detect changed state
	 * Return true if state is equal
	 */
	comparer?(prevState: T, newState: T): boolean;

	/** default false, pass true to also get the interactive messages */
	captureInteractiveMessages?: boolean;
}) {
	const { getState, filterMessage, name, comparer, captureInteractiveMessages } = options;

	const { editor } = useAppContext();

	const filterMessageRef = useRef(filterMessage || (() => true));
	filterMessageRef.current = filterMessage || (() => true);

	const comparerRef = useRef(comparer || (<U>(a: U, b: U) => a === b));
	comparerRef.current = comparer || (<U>(a: U, b: U) => a === b);

	// Only validate messageFilter during Debug mode
	// ----------------------------------------------
	// process.env.DEBUG: Is set at compile time
	// eXpress.isDebugMode: A querystring argument doesn't change during the application
	// filterMessage: is normally always given or never given.
	const shouldDebugMessageListener =
		(process.env.PUBLIC_DEBUG === "true" || eXpress.isDebugMode) &&
		filterMessage &&
		// during tests, we don't want to debug this hook.
		// This caused the test to fail (I assume because not production build and StrictMode runs effects twice)
		// If we don't want the TEST environemnet variable, we could maybe use NODE_ENV === 'development'
		process.env.NODE_ENV !== "test";

	const lastStateRef = useRef<T | undefined>(undefined);

	if (!shouldDebugMessageListener) {
		// Remark: When using React >= v18, we can use useSyncExternalStore, for better concurrent support
		// eslint-disable-next-line react-compiler/react-compiler
		return useSubscription({
			getCurrentValue: useCallback(() => {
				const prevState = lastStateRef.current as T;
				const newState = getState(editor);
				// Requires a stable result to prevent infinite loop
				const isSame = comparerRef.current(prevState, newState);
				if (!isSame) {
					lastStateRef.current = newState;
					return newState;
				}
				return prevState;
			}, [editor, getState]),
			subscribe: useCallback(
				(cb) => {
					return editor.broadcaster.subscribe(
						{
							messageHandler(message) {
								const shouldUpdate = filterMessageRef.current(message); // Use a stable function so we don't re-subscribe when filterMessage changes
								if (shouldUpdate) {
									cb();
								}
							},
						},

						captureInteractiveMessages
							? eXpress.core.ListenerFlags.Interactive
							: eXpress.core.ListenerFlags.None,
					).dispose;
				},
				[captureInteractiveMessages, editor.broadcaster],
			),
		});
	}

	// Detect invalid message filters
	// -------------------------------
	// eslint-disable-next-line react-compiler/react-compiler
	const lastMessageRef = useRef<eXpress.core.IMessage | undefined>(undefined);

	/**
	 * Once we use react 18, we can switch from useSubcription to the standard useSyncExternalStore
	 * https://www.npmjs.com/package/use-subscription
	 */
	// eslint-disable-next-line react-compiler/react-compiler
	return useSubscription({
		getCurrentValue: useCallback(() => {
			const message = lastMessageRef.current;
			const prevState = lastStateRef.current as T;
			const newState = getState(editor);
			const currentState = message ? (lastStateRef.current as T) : newState;
			const shouldUpdate = !message || filterMessageRef.current(message); // Use a stable function so we don't re-subscribe when filterMessage changes
			if (shouldUpdate) {
				// Requires a stable result to prevent infinite loop
				const isSame = comparerRef.current(prevState, newState);
				if (!isSame) {
					lastStateRef.current = newState;
					return newState;
				}
				return prevState;
			}

			// Check if update was needed
			if (message && !comparerRef.current(currentState, newState)) {
				const messageName = eXpress.core.getMessageName(message);

				const errorMessage = `The return state of hook '${name}' is changed. filterMessage should have returned true or specify a custom comparer function.\nLast message: ${messageName}`;
				editor.logger.error(errorMessage);

				const diffs = diffMessage(newState, currentState);
				if (diffs) {
					editor.logger.error(
						`Diffs when comparing the real state with the returned state by value:\n${diffs}`,
					);
				} else {
					const currentStateString = eXpress.utils.toJsonString(currentState);
					const newStateString = eXpress.utils.toJsonString(currentState);

					if (currentStateString === newStateString) {
						editor.logger.error(`New state: ${currentStateString}`); // Log first so not trimmed in error log
						editor.logger.error(
							`No diffs found by value, Check if correct comparer function is used for '${name}'.`,
						);
					} else {
						editor.logger.error(`Current state: ${currentStateString}`);
						editor.logger.error(`Expected state: ${newStateString}`);
					}
				}

				// Log to the server
				const error = new Error(errorMessage);
				error.name = "InvalidHookError";
				editor.client.logErrorAsync(error, false);

				eXpress.debugBreak();
			}
			// Return previous/last state
			return currentState;
		}, [editor, getState, name]),
		subscribe: useCallback(
			(cb) => {
				return editor.broadcaster.subscribe(
					{
						messageHandler(message) {
							lastMessageRef.current = message; // Remember message for in callback
							cb(); // always executed, no filter used
						},
					},
					eXpress.core.ListenerFlags.None,
				).dispose;
			},
			[editor],
		),
	});
}
